import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  IChannel,
  TChannelProtocol,
  TChannelTypeGroup,
} from '@/entities/channel';
import { BillingDialog, ChannelSubscription } from '@/features/billing';
import {
  ChannelIconWithLabel,
  ChannelPendingDialog,
  ChannelsList,
  CreateChannelButton,
  CreateTgBotDialog,
  AvitoConnectForm,
  OzonConnectForm,
  MaxAuthDialog,
  MaxPersonalAuthDialog,
  SelectChannelsDialog,
  SelectChannelTypeDialog,
  SelectCompanyDialog,
  TelegramPersonalAuthDialog,
  WildberriesConnectForm,
  useAddChannelToCompany,
  useChannelFromUrl,
  useChannelsList,
  useDeleteChannel,
  getPersonalChannelPhoneOverride,
  VkAuthDialog,
  WhatsAppPersonalAuthDialog,
} from '@/features/channels';
import { usePageTitle } from '@/features/pageTitle';
import { useChannelsPage } from '@/pages/channels/useChannelsPage';
import { useQueryParams } from '@/shared/lib/useQueryParams';
import { PageContainer } from '@/shared/ui/PageContainer';

import { ActionsMenu } from './ActionsMenu';
import { useChannelDialogsSyncWithChannelIdFromUrl } from './useChannelDialogsSyncWithChannelIdFromUrl';
import { useChannelsDialogs } from './useChannelsDialogs';
import { STEP, useChannelSteps } from './useChannelSteps';

export const ChannelsPage = () => {
  const { t } = useTranslation();

  usePageTitle(t('common:menu.channels'));

  const { channelIdFromUrl } = useChannelFromUrl();
  const { channels, isPending } = useChannelsList();
  const { clearQueryParams } = useQueryParams();
  const { deleteChannel } = useDeleteChannel();
  const { foundChannelById } = useChannelsPage({ channels });
  const { addChannelToCompany } = useAddChannelToCompany();
  const [billingChannel, setBillingChannel] = React.useState<IChannel | null>(
    null
  );

  const foundChannelByIdFromUrl = foundChannelById(channelIdFromUrl);

  const {
    step,
    setStep,
    selectedChannelType,
    selectedChannelProtocol,
    setSelectedChannelType,
    setSelectedChannelProtocol,
  } = useChannelSteps();

  const {
    openPendingChannelDialog,
    setOpenPendingChannelDialog,
    openTgBotDialog,
    setOpenTgBotDialog,
    openTgPersonalDialog,
    setOpenTgPersonalDialog,
    openWhatsAppPersonalDialog,
    setOpenWhatsAppPersonalDialog,
    openMaxDialog,
    setOpenMaxDialog,
    openMaxPersonalDialog,
    setOpenMaxPersonalDialog,
    openAvitoDialog,
    setOpenAvitoDialog,
    openWildberriesDialog,
    setOpenWildberriesDialog,
    openOzonDialog,
    setOpenOzonDialog,
    openVkDialog,
    setOpenVkDialog,
    openAuthorizationDialogByProtocol,
  } = useChannelsDialogs();

  const handleAddChannelToCompany = async (
    channelId: string,
    companyId: string
  ) => {
    if (companyId && channelId) {
      await addChannelToCompany({
        companyId,
        channelId,
      });
    }
  };

  const selectChannelType = (channelType: TChannelTypeGroup) => {
    setSelectedChannelType(channelType);
    setStep(STEP.SELECT_CHANNEL_PROTOCOL);
  };

  const selectChannelProtocol = (channel: TChannelProtocol) => {
    setSelectedChannelProtocol(channel);
    setStep(STEP.BILLING);
  };

  const openSelectCompanyDialog = () => {
    if (foundChannelByIdFromUrl?.id) {
      setStep(STEP.SELECT_COMPANY);
    }
  };

  useChannelDialogsSyncWithChannelIdFromUrl({
    channelIdFromUrl,
    foundChannelByIdFromUrl,
    step,
    setOpenPendingChannelDialog,
    clearQueryParams,
    openAuthorizationDialogByProtocol,
  });

  return (
    <PageContainer>
      {channels ? (
        <CreateChannelButton
          onClick={() => setStep(STEP.SELECT_CHANNEL_TYPE)}
          sx={{ marginBottom: '16px' }}
        />
      ) : null}
      <ChannelsList
        channels={channels}
        isPending={isPending}
        listNotFoundActionSlot={
          <CreateChannelButton
            onClick={() => setStep(STEP.SELECT_CHANNEL_TYPE)}
          />
        }
        renderTariff={(channel: IChannel) => (
          <ChannelSubscription
            channelId={channel.id}
            phoneOverride={getPersonalChannelPhoneOverride(channel.protocol, channel.phone_number)}
          />
        )}
        renderActions={(channel: IChannel) => (
          <ActionsMenu channel={channel} onDelete={deleteChannel} />
        )}
        onPaySubscription={setBillingChannel}
        onRenewSubscription={setBillingChannel}
      />

      <SelectChannelTypeDialog
        open={step === STEP.SELECT_CHANNEL_TYPE}
        onClose={() => setStep(STEP.CLOSED)}
        onSelected={selectChannelType}
      />

      <SelectChannelsDialog
        open={step === STEP.SELECT_CHANNEL_PROTOCOL}
        onClose={() => setStep(STEP.CLOSED)}
        onBack={() => setStep(STEP.SELECT_CHANNEL_TYPE)}
        onSelected={selectChannelProtocol}
        channelType={selectedChannelType}
      />

      <BillingDialog
        key={selectedChannelProtocol}
        open={step === STEP.BILLING}
        onClose={() => setStep(STEP.CLOSED)}
        onBack={() => setStep(STEP.SELECT_CHANNEL_PROTOCOL)}
        channelProtocol={selectedChannelProtocol}
        renderIconWithLabel={(renderLabelByProtocol) =>
          selectedChannelProtocol ? (
            <ChannelIconWithLabel
              label={renderLabelByProtocol(selectedChannelProtocol)}
              protocol={selectedChannelProtocol}
            />
          ) : null
        }
      />

      <BillingDialog
        key={billingChannel?.id}
        open={Boolean(billingChannel)}
        onClose={() => setBillingChannel(null)}
        onBack={() => setBillingChannel(null)}
        channelProtocol={billingChannel?.protocol}
        isRenewing
        channelId={billingChannel?.id}
        renderIconWithLabel={(renderLabelByProtocol) =>
          billingChannel ? (
            <ChannelIconWithLabel
              label={renderLabelByProtocol(billingChannel.protocol)}
              protocol={billingChannel.protocol}
            />
          ) : null
        }
      />

      {foundChannelByIdFromUrl?.id ? (
        <SelectCompanyDialog
          open={step === STEP.SELECT_COMPANY}
          onClose={() => {
            clearQueryParams();
            setStep(STEP.CLOSED);
          }}
          onSubmit={async ({ companyId }) => {
            await handleAddChannelToCompany(
              foundChannelByIdFromUrl.id,
              companyId
            );
            clearQueryParams();
            setStep(STEP.CLOSED);
          }}
          currentCompanyId={foundChannelByIdFromUrl.company_id}
        />
      ) : null}

      {foundChannelByIdFromUrl ? (
        <>
          <CreateTgBotDialog
            open={openTgBotDialog}
            onClose={() => {
              setOpenTgBotDialog(false);
              clearQueryParams();
            }}
            channelId={foundChannelByIdFromUrl.id}
            onCreated={() => {
              setOpenTgBotDialog(false);
              openSelectCompanyDialog();
            }}
          />

          <TelegramPersonalAuthDialog
            open={openTgPersonalDialog}
            onClose={() => {
              setOpenTgPersonalDialog(false);
              clearQueryParams();
            }}
            onAuthorize={() => {
              setOpenTgPersonalDialog(false);
              openSelectCompanyDialog();
            }}
            channel={foundChannelByIdFromUrl}
          />

          <WhatsAppPersonalAuthDialog
            open={openWhatsAppPersonalDialog}
            onClose={() => {
              setOpenWhatsAppPersonalDialog(false);
              clearQueryParams();
            }}
            onAuthorize={() => {
              setOpenWhatsAppPersonalDialog(false);
              openSelectCompanyDialog();
            }}
            channel={foundChannelByIdFromUrl}
          />

          <MaxAuthDialog
            open={openMaxDialog}
            onClose={() => {
              setOpenMaxDialog(false);
              clearQueryParams();
            }}
            channelId={foundChannelByIdFromUrl.id}
            onCreated={() => {
              setOpenMaxDialog(false);
              openSelectCompanyDialog();
            }}
          />

          <MaxPersonalAuthDialog
            open={openMaxPersonalDialog}
            onClose={() => {
              setOpenMaxPersonalDialog(false);
              clearQueryParams();
            }}
            onAuthorize={() => {
              setOpenMaxPersonalDialog(false);
              openSelectCompanyDialog();
            }}
            channel={foundChannelByIdFromUrl}
          />

          <AvitoConnectForm
            open={openAvitoDialog}
            onClose={() => {
              setOpenAvitoDialog(false);
              clearQueryParams();
            }}
            onAuthorize={() => {
              setOpenAvitoDialog(false);
              openSelectCompanyDialog();
            }}
            channel={foundChannelByIdFromUrl}
          />

          <WildberriesConnectForm
            open={openWildberriesDialog}
            onClose={() => {
              setOpenWildberriesDialog(false);
              clearQueryParams();
            }}
            onAuthorize={() => {
              setOpenWildberriesDialog(false);
              openSelectCompanyDialog();
            }}
            channel={foundChannelByIdFromUrl}
          />

          <OzonConnectForm
            open={openOzonDialog}
            onClose={() => {
              setOpenOzonDialog(false);
              clearQueryParams();
            }}
            onAuthorize={() => {
              setOpenOzonDialog(false);
              openSelectCompanyDialog();
            }}
            channel={foundChannelByIdFromUrl}
          />

          <VkAuthDialog
            open={openVkDialog}
            onClose={() => {
              setOpenVkDialog(false);
              clearQueryParams();
            }}
            channelId={foundChannelByIdFromUrl.id}
            onCreated={() => {
              setOpenVkDialog(false);
              openSelectCompanyDialog();
            }}
          />
        </>
      ) : null}

      <ChannelPendingDialog
        open={openPendingChannelDialog}
        onClose={() => {
          setOpenPendingChannelDialog(false);
        }}
      />
    </PageContainer>
  );
};
